import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { format, startOfMonth, endOfMonth, addMonths, eachDayOfInterval, parseISO } from 'date-fns';
import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  ApiError,
  AssignmentRequest,
  BasicFacility,
  ConnectedFacilityPublic,
  IdentifyHealthcareUserResponse,
  LicenseStateEnum,
  Shift,
  AssignmentsService,
  ConnectedFacilityPublicService,
  ConnectedFacilityService,
  HealthcareEntitiesService,
  ProviderAvailabilityPublicService,
  ShiftRateTypesEnum,
  ShiftTypesEnum,
  State442Enum,
  ProviderAvailability,
} from '../../../../../js/api';
import { SessionContext } from '../../../../../js/app/contexts/SessionContext';
import { bookingRequestsQueryKeys } from '../../../../../js/app/query-keys-factories/booking-requests';
import { BaseCalendar } from '../../../components/base-calendar';
import { Button } from '../../../components/button';
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogDescription,
} from '../../../components/dialog';
import { Field, Fieldset, FieldGroup, Description } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { reactSelectReset } from '../../../utils/utils';
import { SchedulePreview, type ShiftEntry } from './SchedulePreview';
import { ShiftConfiguration, type DateShiftConfig, type ShiftSettings } from './ShiftConfiguration';

type ModalState = 'email' | 'facility-select' | 'manual-input';

const MODAL_STATE = {
  EMAIL: 'email' as const,
  FACILITY_SELECT: 'facility-select' as const,
  MANUAL_INPUT: 'manual-input' as const,
} satisfies Record<string, ModalState>;

const DATE_FORMAT = 'yyyy-MM-dd';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ProviderAvailabilityWrapper: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [currentMonth, setCurrentMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userResponse, setUserResponse] = useState<IdentifyHealthcareUserResponse | null>(null);
  const [tentativeFacility, setTentativeFacility] = useState<BasicFacility | null>(null);
  const [connectedFacility, setConnectedFacility] = useState<ConnectedFacilityPublic | null>(null);
  const [shiftConfigs, setShiftConfigs] = useState<Record<string, DateShiftConfig>>({});
  const [potentialSchedule, setPotentialSchedule] = useState<Record<
    string,
    DateShiftConfig
  > | null>(null);
  const [potentialScheduleShiftSettings, setPotentialScheduleShiftSettings] =
    useState<ShiftSettings | null>(null);
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [assignmentSubmitError, setAssignmentSubmitError] = useState<string | null>(null);
  const [shiftErrors, setShiftErrors] = useState<Record<number, string[]>>({});
  const [showThankYou, setShowThankYou] = useState(false);
  const [assignmentCreated, setAssignmentCreated] = useState<boolean | null>(null);
  const [initialAssignmentRequest, setInitialAssignmentRequest] =
    useState<Partial<AssignmentRequest> | null>(null);
  const [initialUserResponse, setInitialUserResponse] =
    useState<IdentifyHealthcareUserResponse | null>(null);
  const [isEmailChanging, setIsEmailChanging] = useState(false);

  const { user } = useContext(SessionContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Reset local fetch tracking and query cache when user changes (e.g. login/logout)
  useEffect(() => {
    setAllAvailableDates([]);
    setFetchedRanges([]);
    queryClient.invalidateQueries({ queryKey: ['providerAvailabilityData', token] });
  }, [user?.id, token, queryClient]);

  // Single source of truth for assignment request data
  const [assignmentRequest, setAssignmentRequest] = useState<Partial<AssignmentRequest>>({
    provider: null,
    email: '',
    first_name: '',
    last_name: '',
    title: null,
    healthcare_entity_rep: null,
    facility: null,
    facility_name: null,
    facility_city: null,
    facility_state: null,
    shifts: [],
    comment: null,
  });

  const [allAvailableDates, setAllAvailableDates] = useState<string[]>([]);
  const [fetchedRanges, setFetchedRanges] = useState<{ start: string; end: string }[]>([]);

  // Calculate date range based on current month and number of months displayed
  const dateRange = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(addMonths(currentMonth, 1)); // numberOfMonths is 2, so add 1 month
    return {
      startOn: format(start, DATE_FORMAT),
      endOn: format(end, DATE_FORMAT),
    };
  }, [currentMonth]);

  const isAlreadyFetched = useMemo(() => {
    const start = parseISO(dateRange.startOn);
    const end = parseISO(dateRange.endOn);

    return fetchedRanges.some((range) => {
      const rangeStart = parseISO(range.start);
      const rangeEnd = parseISO(range.end);
      return start >= rangeStart && end <= rangeEnd;
    });
  }, [dateRange, fetchedRanges]);

  // Use the cached provider availability data if we're not fetching new data
  const {
    data: providerAvailabilityData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['providerAvailabilityData', token, dateRange.startOn, dateRange.endOn],
    queryFn: async () => {
      if (!token) {
        throw new Error('Token is required');
      }
      const data = await ProviderAvailabilityPublicService.providerAvailabilityPublicRetrieve({
        availabilityToken: token,
        startOn: dateRange.startOn,
        endOn: dateRange.endOn,
      });

      if (data.available_dates) {
        setAllAvailableDates((prev) => {
          const newDates = data.available_dates as unknown as string[];
          const combined = [...prev, ...newDates];
          return Array.from(new Set(combined));
        });
      }

      setFetchedRanges((prev) => [...prev, { start: dateRange.startOn, end: dateRange.endOn }]);

      return data;
    },
    enabled: !!token && !isAlreadyFetched,
    // Stale time and cache time are handled by the isAlreadyFetched logic manually,
    // but we want to keep the last data available while we check if we need to fetch.
    initialData: () => {
      // If we've already fetched this range, we can return the cached data from the queryClient
      // but since we're using allAvailableDates and other state, we mostly care about
      // providerAvailabilityData not being null for the provider info.
      return undefined;
    },
  });

  // Keep track of the latest provider availability data for basic info (id, display_name, etc)
  const [lastProviderData, setLastProviderData] = useState<any>(null);
  useEffect(() => {
    if (providerAvailabilityData) {
      setLastProviderData(providerAvailabilityData);
    }
  }, [providerAvailabilityData]);

  const effectiveProviderData: ProviderAvailability = providerAvailabilityData || lastProviderData;

  const availableDates = allAvailableDates;

  // Calculate disabled dates - dates from the start of the current month until today
  const disabledDates = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startOfThisMonth = startOfMonth(today);

    // If today is the 1st, there are no past dates in the current month to disable
    if (startOfThisMonth >= today) {
      return [];
    }

    return eachDayOfInterval({
      start: startOfThisMonth,
      end: today,
    });
  }, []);

  // Update provider with availability token when token is available
  useEffect(() => {
    if (effectiveProviderData?.availability_token) {
      setAssignmentRequest((prev) => ({
        ...prev,
        provider: effectiveProviderData.availability_token,
      }));
    }
  }, [effectiveProviderData?.availability_token]);

  // Fetch connected facility details when a facility with an ID is selected
  // For facilities without IDs (anonymous users), use default values
  useEffect(() => {
    const facilityId = assignmentRequest.facility;
    const facilityName = assignmentRequest.facility_name?.trim();

    // Default values for anonymous users (facilities without IDs)
    // at some point the backend is going to use the ShiftTypesEnum instead of ShiftRateTypesEnum
    // to populate shift_rate_types, so this will need to do the same
    const defaultConnectedFacility: ConnectedFacilityPublic = {
      shift_rate_types: [
        ShiftRateTypesEnum.DAY,
        ShiftRateTypesEnum.NIGHT,
        ShiftRateTypesEnum._24H,
        ShiftRateTypesEnum.CALL,
      ],
      shift_length: 12,
      shift_start_day: '07:00:00',
      shift_start_night: '19:00:00',
      shift_start_24h: '07:00:00',
    };

    // If no facility ID and no facility name, clear connectedFacility
    if (!facilityId && !facilityName) {
      setConnectedFacility(null);
      return;
    }

    // If we have a facility name but no ID (anonymous user), use defaults
    if (!facilityId && facilityName) {
      setConnectedFacility(defaultConnectedFacility);
      return;
    }

    // If we have a facility ID, fetch connected facility details
    if (facilityId && effectiveProviderData?.availability_token) {
      ConnectedFacilityPublicService.connectedFacilityPublicLookupRetrieve({
        facility: facilityId,
        availabilityToken: effectiveProviderData.availability_token,
      })
        .then((connectedFacilityData) => {
          setConnectedFacility(connectedFacilityData);
        })
        .catch((error) => {
          if (error instanceof ApiError && error.status === 404) {
            // Initialize with default values when facility is not found
            setConnectedFacility(defaultConnectedFacility);
          } else {
            console.error('Error fetching connected facility details:', error);
            setConnectedFacility(defaultConnectedFacility);
          }
        });
    }
  }, [assignmentRequest.facility, assignmentRequest.facility_name, effectiveProviderData]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleCreateRequestClick = async () => {
    const ERROR_MESSAGE = 'Error identifying healthcare user:';
    // Check if user is logged in
    if (user) {
      // User is logged in - populate assignmentRequest with user info
      const userEmail = user.email;
      setAssignmentRequest((prev) => ({
        ...prev,
        email: userEmail,
        first_name: user.first_name || prev.first_name || '',
        last_name: user.last_name || prev.last_name || '',
        healthcare_entity_rep: user.healthcare_entity_rep_id || prev.healthcare_entity_rep || null,
      }));

      // Fetch facilities for logged-in user using ConnectedFacilityService
      setIsSubmitting(true);
      try {
        // Fetch user info first
        const userInfoResponse =
          await HealthcareEntitiesService.healthcareEntitiesIdentifyHealthcareUserCreate({
            requestBody: { email: userEmail },
          });

        // Fetch connected facilities
        // Filter by provider if available (since we're on a provider availability page)
        const connectedFacilitiesResponse = await ConnectedFacilityService.connectedFacilityList({
          ...(effectiveProviderData?.id && {
            provider: [effectiveProviderData.id],
            statusCategory: 'completed',
          }),
          // The API will automatically filter by the logged-in user's healthcare_entity_rep
        });

        // Transform ConnectedFacility results to BasicFacility format
        const facilities = connectedFacilitiesResponse.results.map((connectedFacility) => ({
          id: connectedFacility.facility.id,
          name: connectedFacility.facility.name,
          city: connectedFacility.facility.city,
          state: connectedFacility.facility.state,
        }));

        // Merge facilities into userResponse
        const response = {
          ...userInfoResponse,
          facilities,
        };
        setUserResponse(response);
      } catch (error) {
        console.error(ERROR_MESSAGE, error);
        // If fetch fails, still open modal but user will need to enter email manually
        setAssignmentRequest((prev) => ({ ...prev, email: userEmail }));
      } finally {
        setIsSubmitting(false);
      }
    }

    setIsModalOpen(true);
    setInitialAssignmentRequest({ ...assignmentRequest });
    setInitialUserResponse(userResponse);
    // Initialize tentative facility from current assignmentRequest facility data
    if (assignmentRequest.facility) {
      // If we have a facility ID, we'd need to reconstruct BasicFacility from assignmentRequest
      // For now, set tentativeFacility to null and let the modal handle it
      setTentativeFacility(null);
    } else if (assignmentRequest.facility_name) {
      // If there's no facility ID but there's a facility name, it was manually entered
      // Mark it as "Other" so it shows correctly in the dropdown
      setTentativeFacility({
        id: 'OTHER' as any,
        name: assignmentRequest.facility_name,
        city: assignmentRequest.facility_city || '',
        state: assignmentRequest.facility_state
          ? (assignmentRequest.facility_state as unknown as State442Enum)
          : undefined,
      });
    } else {
      setTentativeFacility(null);
    }
    // If we have an email but no userResponse (and user is not logged in), fetch facilities
    // Also re-fetch if userResponse exists but is for a different email (stale data)
    const emailMismatch =
      userResponse &&
      assignmentRequest.email?.trim() &&
      userResponse.email !== assignmentRequest.email.trim();
    if (emailMismatch) {
      // Clear stale userResponse for different email
      setUserResponse(null);
    }
    const shouldRefetch =
      !user && assignmentRequest.email?.trim() && (!userResponse || emailMismatch);
    if (shouldRefetch && assignmentRequest.email) {
      setIsSubmitting(true);
      try {
        const response =
          await HealthcareEntitiesService.healthcareEntitiesIdentifyHealthcareUserCreate({
            requestBody: { email: assignmentRequest.email.trim() },
          });
        setUserResponse(response);
        // Update assignmentRequest with user info from response
        setAssignmentRequest((prev) => ({
          ...prev,
          first_name: response.first_name || prev.first_name || '',
          last_name: response.last_name || prev.last_name || '',
          healthcare_entity_rep: response.healthcare_entity_rep_id || null,
        }));
      } catch (error) {
        console.error(ERROR_MESSAGE, error);
        // If fetch fails, clear email so user can re-enter
        setAssignmentRequest((prev) => ({ ...prev, email: '' }));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Reset tentative facility when closing without saving
    setTentativeFacility(null);
  };

  const handleModalCancel = () => {
    // Check if email changed before restoring state
    const emailChanged = initialAssignmentRequest?.email !== assignmentRequest.email;

    // Restore initial state if it exists
    if (initialAssignmentRequest) {
      setAssignmentRequest(initialAssignmentRequest);
      setInitialAssignmentRequest(null);
    }
    // Only restore userResponse if the email hasn't changed
    // (if email changed, the userResponse is for a different email and shouldn't be restored)
    if (initialUserResponse !== null && !emailChanged) {
      setUserResponse(initialUserResponse);
    } else if (emailChanged) {
      // Clear userResponse if email changed, as it's for a different email
      setUserResponse(null);
    }
    setInitialUserResponse(null);
    setIsEmailChanging(false);
    handleModalClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Don't submit if we're showing facility selection or manual facility input
    if (assignmentRequest.email?.trim() && userResponse && !isEmailChanging) {
      return;
    }
    if (!assignmentRequest.email?.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response =
        await HealthcareEntitiesService.healthcareEntitiesIdentifyHealthcareUserCreate({
          requestBody: { email: assignmentRequest.email.trim() },
        });
      setUserResponse(response);
      setIsEmailChanging(false);
      // Update assignmentRequest with user info from response
      setAssignmentRequest((prev) => ({
        ...prev,
        first_name: response.first_name || prev.first_name || '',
        last_name: response.last_name || prev.last_name || '',
        healthcare_entity_rep: response.healthcare_entity_rep_id || null,
      }));
      // Don't close the modal - let user select facility or enter manual facility info
    } catch (error) {
      console.error('Error identifying healthcare user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Transform facilities into react-select options
  const facilityOptions = useMemo(() => {
    if (!userResponse?.facilities) return [];
    const facilityOpts = userResponse.facilities.map((facility) => ({
      value: facility.id || '',
      label: facility.name || 'Unnamed Facility',
      facility: facility,
    }));
    // Add "Other" option at the end only if user doesn't have an account
    // (i.e., when healthcare_entity_rep_id is not present, meaning we'll send email, not auto-create)
    const hasAccount = !!userResponse?.healthcare_entity_rep_id;
    if (hasAccount) {
      return facilityOpts;
    }
    return [
      ...facilityOpts,
      {
        value: 'OTHER',
        label: 'Other',
        facility: null,
      },
    ];
  }, [userResponse?.facilities, userResponse?.healthcare_entity_rep_id]);

  const tentativeFacilityOption = useMemo(() => {
    if (!tentativeFacility) return null;
    // Check if it's the "Other" option
    if (tentativeFacility.id === 'OTHER') {
      return facilityOptions.find((option) => option.value === 'OTHER') || null;
    }
    return facilityOptions.find((option) => option.facility?.id === tentativeFacility.id) || null;
  }, [tentativeFacility, facilityOptions]);

  // Check if "Other" option is selected
  const isOtherFacilitySelected = tentativeFacilityOption?.value === 'OTHER';

  // Clear "Other" selection if user now has an account (edge case: account status changed)
  useEffect(() => {
    if (
      tentativeFacility?.id === 'OTHER' &&
      userResponse?.healthcare_entity_rep_id &&
      !facilityOptions.find((option) => option.value === 'OTHER')
    ) {
      setTentativeFacility(null);
    }
  }, [tentativeFacility, userResponse?.healthcare_entity_rep_id, facilityOptions]);

  // Determine which modal state we're in
  const modalState: ModalState = useMemo(() => {
    if (isEmailChanging || !assignmentRequest.email?.trim() || !userResponse) {
      return MODAL_STATE.EMAIL;
    }
    if (userResponse.facilities && userResponse.facilities.length > 0) {
      return MODAL_STATE.FACILITY_SELECT;
    }
    return MODAL_STATE.MANUAL_INPUT;
  }, [assignmentRequest.email, userResponse, isEmailChanging]);

  // Validate assignment submission
  const validateAssignmentSubmission = (shiftEntries: ShiftEntry[]): string | null => {
    if (shiftEntries.length === 0) {
      return 'Please add at least one shift';
    }
    if (!assignmentRequest.email?.trim()) {
      return 'Email is required';
    }
    if (!assignmentRequest.facility && !assignmentRequest.facility_name?.trim()) {
      return 'Facility is required';
    }
    if (!assignmentRequest.first_name?.trim() || !assignmentRequest.last_name?.trim()) {
      return 'First name and last name are required';
    }
    return null;
  };

  // Transform shift entries to Shift objects
  const transformShifts = (shiftEntries: ShiftEntry[]): Shift[] => {
    const TIME_FORMAT = 'HH:mm:ss';
    return shiftEntries.map((entry) => ({
      shift_type: entry.shiftType as ShiftTypesEnum,
      shift_rate_type: (entry.onCall
        ? ShiftRateTypesEnum.CALL
        : entry.shiftType) as ShiftRateTypesEnum,
      start_on: format(entry.startTime, DATE_FORMAT),
      start_time: format(entry.startTime, TIME_FORMAT),
      end_on: format(entry.endTime, DATE_FORMAT),
      end_time: format(entry.endTime, TIME_FORMAT),
    }));
  };

  // Build final AssignmentRequest object for submission
  const buildFinalAssignmentRequest = (shifts: Shift[], comment: string): AssignmentRequest => {
    if (!assignmentRequest.facility && !assignmentRequest.facility_name?.trim()) {
      throw new Error('Facility is required');
    }

    return {
      provider: effectiveProviderData?.availability_token || null,
      ...assignmentRequest,
      shifts,
      comment: comment.trim() || '',
    } as Partial<AssignmentRequest> as AssignmentRequest;
  };

  // Handle assignment submission
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleAssignmentSubmit = async (shiftEntries: ShiftEntry[], comment: string) => {
    const validationError = validateAssignmentSubmission(shiftEntries);
    if (validationError) {
      setAssignmentSubmitError(validationError);
      return;
    }

    setIsSubmittingAssignment(true);
    setAssignmentSubmitError(null);
    setShiftErrors({});

    try {
      const shifts = transformShifts(shiftEntries);
      const finalRequest = buildFinalAssignmentRequest(shifts, comment);

      const response = (await AssignmentsService.assignmentsRequestsCreate({
        requestBody: finalRequest,
      })) as AssignmentRequest & { $responseStatus?: number };
      const status = response.$responseStatus;
      if (status === 201) {
        setAssignmentCreated(true);
      } else if (status === 202) {
        setAssignmentCreated(false);
      }
      // Check if user is logged in
      if (user) {
        queryClient.invalidateQueries({
          queryKey: bookingRequestsQueryKeys.allLists(),
        });
        // User is logged in - show toast and redirect
        toast.success('Assignment request submitted successfully!');
        navigate('/app/healthcare-entity/booking-requests?page=1&status__in=PENDING&date=UPCOMING');
      } else {
        // User is not logged in - show thank you message
        setShowThankYou(true);
      }
    } catch (error) {
      console.error('Error submitting assignment request:', error);

      // Check if error has shifts key
      if (error instanceof ApiError && error.body?.shifts && Array.isArray(error.body.shifts)) {
        // Parse shift errors - index matches shift entry index
        const parsedShiftErrors: Record<number, string[]> = {};

        error.body.shifts.forEach((shiftErrorObj: Record<string, string[]>, index: number) => {
          // Skip empty objects
          if (
            shiftErrorObj &&
            typeof shiftErrorObj === 'object' &&
            Object.keys(shiftErrorObj).length > 0
          ) {
            // Collect all error messages from all date keys in this shift error object
            const errorMessages: string[] = [];
            Object.values(shiftErrorObj).forEach((dateErrors) => {
              if (Array.isArray(dateErrors)) {
                errorMessages.push(...dateErrors);
              }
            });

            if (errorMessages.length > 0) {
              parsedShiftErrors[index] = errorMessages;
            }
          }
        });

        setShiftErrors(parsedShiftErrors);
      } else {
        // No shifts key - log the whole error object
        console.error('Full error object:', error);
        setAssignmentSubmitError(
          error instanceof Error
            ? error.message
            : 'Failed to submit assignment request. Please try again.'
        );
      }
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  if (!token) {
    return (
      <div className="tw:p-4 tw:bg-zinc-100 tw:md:bg-transparent tw:max-w-6xl tw:mx-auto tw:w-full">
        <div className="tw:bg-white tw:rounded-xl tw:p-8 tw:border tw:border-zinc-200 tw:text-center">
          <p className="tw:text-stoplight-danger">Invalid token</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw:p-4 tw:bg-zinc-100 tw:md:bg-transparent tw:max-w-6xl tw:mx-auto tw:w-full">
        <div className="tw:bg-white tw:rounded-xl tw:p-8 tw:border tw:border-zinc-200 tw:text-center">
          <p className="tw:text-stoplight-danger">Error loading provider availability</p>
        </div>
      </div>
    );
  }

  if (!effectiveProviderData && !isLoading) {
    return (
      <div className="tw:p-4 tw:bg-zinc-100 tw:md:bg-transparent tw:max-w-6xl tw:mx-auto tw:w-full">
        <div className="tw:bg-white tw:rounded-xl tw:p-8 tw:border tw:border-zinc-200 tw:text-center">
          <p className="tw:text-zinc-500">No data available</p>
        </div>
      </div>
    );
  }

  // Show Thank You message for non-logged-in users after successful submission
  if (showThankYou) {
    return (
      <div className="tw:p-4  tw:max-w-4xl tw:mx-auto tw:w-full">
        <div className="tw:bg-white tw:rounded-xl tw:p-8 tw:border tw:border-zinc-200 tw:text-center">
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-4">
            {/* Success Icon */}
            <div className="tw:h-20 tw:w-20 tw:flex tw:items-center tw:justify-center tw:text-brand-green-0 tw:!text-8xl tw:mb-4">
              <i className="fa-solid fa-badge-check" />
            </div>

            {/* Thank You Message */}
            <div className="tw:max-w-md">
              <h2 className="tw:text-2xl tw:font-semibold tw:text-zinc-900 tw:mb-3">Thank You!</h2>
              <p className="tw:text-base tw:text-zinc-500">
                {assignmentCreated
                  ? `Your request has been created`
                  : `Your request has been received`}
              </p>
              <p className="tw:text-base tw:text-zinc-700">
                {assignmentCreated
                  ? `${effectiveProviderData?.display_name} is being notified of your request and you will be notified when they respond.`
                  : `We will review your assignment request for ${effectiveProviderData?.display_name} and get back to you as soon as possible.`}
              </p>
              <div className="tw:flex tw:flex-col tw:items-start tw:justify-start tw:gap-2 tw:max-w-fit tw:mx-auto tw:mt-6">
                <div className="tw:flex tw:items-start tw:justify-start tw:gap-4">
                  <i className="fa-solid fa-envelope tw:text-zinc-400 tw:mt-1" />
                  <a
                    href={`mailto:${window.SENDIT_CONTACT_INFO.healthcare_entity_support_email}`}
                    className="tw:text-brand-green-1 tw:!no-underline tw:text-left tw:break-all"
                  >
                    {window.SENDIT_CONTACT_INFO.healthcare_entity_support_email}
                  </a>
                </div>
                <div className="tw:flex tw:items-start tw:justify-start tw:gap-4">
                  <i className="fa-solid fa-phone tw:text-zinc-400 tw:mt-1" />
                  <a
                    href={`tel:${window.SENDIT_CONTACT_INFO.healthcare_entity_phone_text}`}
                    className="tw:text-brand-green-1 tw:!no-underline tw:text-left"
                  >
                    {window.SENDIT_CONTACT_INFO.healthcare_entity_phone_text}
                  </a>
                </div>
              </div>
              <div className="tw:mt-8">
                <Button color="brandgreen0" onClick={() => window.location.reload()}>
                  Request More Dates
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw:p-4 tw:max-w-6xl tw:mx-auto tw:w-full">
      <div className="tw:flex tw:flex-col-reverse tw:lg:flex-row tw:gap-4 tw:lg:gap-2">
        {/* Calendar and Scheduler */}
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div
            className={clsx(
              'tw:md:bg-white tw:rounded-xl tw:md:p-4 tw:md:border tw:border-zinc-200 tw:relative',
              potentialSchedule && potentialScheduleShiftSettings && 'tw:hidden'
            )}
          >
            {isLoading && (
              <div className="tw:absolute tw:inset-0 tw:bg-white/50 tw:z-10 tw:flex tw:items-center tw:justify-center tw:rounded-xl">
                <div className="tw:animate-spin tw:rounded-full tw:h-8 tw:w-8 tw:border-2 tw:border-zinc-300 tw:border-t-brand-green-0" />
              </div>
            )}
            <div className="tw:flex tw:justify-between tw:items-center">
              <div className="tw:flex tw:flex-col tw:items-start tw:justify-center">
                <h2 className="tw:!text-base tw:!font-semibold tw:!text-brand-green-0 tw:!mb-1">
                  Select Available Days
                </h2>
                <p className="tw:hidden tw:md:block tw:!text-xs tw:font-semibold tw:!text-zinc-500">
                  Hold Shift + click to select ranges of days
                </p>
              </div>
              <div className="tw:flex tw:justify-end tw:items-center tw:gap-4">
                {/* available and selected dates color legend */}
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="tw:w-4 tw:h-4 tw:rounded tw:bg-brand-green-2 tw:border-solid tw:border tw:border-brand-green-1/50" />
                  <span className="tw:text-xs tw:text-zinc-600">Available</span>
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <div className="tw:w-4 tw:h-4 tw:rounded tw:bg-brand-green-0" />
                  <span className="tw:text-xs tw:text-zinc-600">Selected</span>
                </div>
              </div>
            </div>
            <BaseCalendar
              availableDates={availableDates}
              selectedDates={selectedDates}
              onSelectedDatesChange={setSelectedDates}
              numberOfMonths={2}
              disabled={!!potentialSchedule}
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              disabledDates={disabledDates}
              className="availability-calendar justify-self-center"
            />
            {!assignmentRequest.facility && !assignmentRequest.facility_name && (
              <div className="tw:flex tw:justify-end tw:my-4">
                <Button color="brand-green-outline" onClick={handleCreateRequestClick}>
                  Create Request
                </Button>
              </div>
            )}
          </div>
          {/* Shift Configuration or Schedule Preview */}
          {(assignmentRequest.facility || assignmentRequest.facility_name?.trim()) &&
            (potentialSchedule && potentialScheduleShiftSettings ? (
              <SchedulePreview
                selectedDates={selectedDates}
                shiftConfigs={potentialSchedule}
                shiftSettings={potentialScheduleShiftSettings}
                onBack={() => {
                  setPotentialSchedule(null);
                  setPotentialScheduleShiftSettings(null);
                  setShiftErrors({});
                }}
                onRemoveDate={(dateKey) => {
                  // Remove date from selectedDates
                  setSelectedDates((prev) =>
                    prev.filter((date) => format(date, DATE_FORMAT) !== dateKey)
                  );
                  // Remove date from potentialSchedule
                  setPotentialSchedule((prev) => {
                    if (!prev) return null;
                    const updated = { ...prev };
                    delete updated[dateKey];
                    return Object.keys(updated).length > 0 ? updated : null;
                  });
                }}
                onSubmit={handleAssignmentSubmit}
                isSubmitting={isSubmittingAssignment}
                submitError={assignmentSubmitError}
                shiftErrors={shiftErrors}
              />
            ) : (
              <ShiftConfiguration
                connectedFacility={connectedFacility}
                selectedDates={selectedDates}
                shiftConfigs={shiftConfigs}
                onShiftConfigChange={setShiftConfigs}
                onPreviewSchedule={(config, shiftSettings) => {
                  setPotentialSchedule(config);
                  setPotentialScheduleShiftSettings(shiftSettings);
                }}
                onRemoveAllDates={() => {
                  setSelectedDates([]);
                  setShiftConfigs({});
                }}
              />
            ))}
        </div>
        {/* Provider and Facility info */}
        <aside className="tw:md:bg-white tw:md:rounded-xl tw:md:py-4 tw:lg:p-6 tw:border-b tw:pb-3 tw:md:border tw:border-zinc-200 tw:flex tw:flex-row tw:lg:flex-col tw:gap-1 tw:md:gap-4 tw:lg:min-w-64 tw:h-fit">
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:md:gap-3 tw:flex-1">
            {/* Avatar */}
            <div className="tw:h-12 tw:w-12 tw:md:h-16 tw:md:w-16 tw:rounded-full tw:bg-brand-green-0/10 tw:flex tw:items-center tw:justify-center tw:text-brand-green-0 tw:text-lg tw:md:text-2xl">
              <i className="fas fa-xl fa-user-doctor" />
            </div>

            {/* Provider Name */}
            <div className="tw:text-center tw:min-h-[3rem] tw:flex tw:flex-col tw:justify-center">
              {effectiveProviderData ? (
                <>
                  <p className="tw:text-xs tw:md:text-sm tw:font-semibold tw:text-zinc-900 tw:!mb-1">
                    {effectiveProviderData.display_name}
                  </p>

                  {/* Specialty */}
                  {effectiveProviderData.specialties &&
                    effectiveProviderData.specialties.length > 0 && (
                      <p className="tw:text-[8pt] tw:md:text-xs tw:text-zinc-500 tw:!mb-0">
                        {effectiveProviderData.specialties[0]}
                      </p>
                    )}
                </>
              ) : (
                <div className="tw:h-4 tw:w-24 tw:bg-zinc-200 tw:animate-pulse tw:rounded tw:mx-auto" />
              )}
            </div>

            {/* Phone Number */}
            {effectiveProviderData?.phone_number && (
              <div className="tw:flex tw:items-center tw:justify-center tw:gap-2 tw:text-xs tw:md:text-sm">
                <i className="tw:!hidden tw:md:!block fa-solid fa-phone tw:text-zinc-400" />
                <a
                  href={`tel:${effectiveProviderData?.phone_number}`}
                  className=" tw:text-zinc-700 hover:tw:text-zinc-900 tw:!no-underline"
                >
                  {effectiveProviderData?.phone_number}
                </a>
              </div>
            )}
          </div>

          {/* User */}
          {assignmentRequest.email && !user && (
            <div className="tw:flex-1 tw:border-l tw:border-zinc-200 tw:pl-4 tw:lg:border-l-0 tw:lg:pl-0 tw:lg:border-t tw:lg:pt-4">
              <div className="tw:flex tw:flex-col tw:items-center tw:text-center">
                <div className="tw:h-12 tw:w-12 tw:md:h-16 tw:md:w-16  tw:rounded-full tw:bg-brand-green-0/10 tw:flex tw:items-center tw:justify-center tw:text-brand-green-0 tw:text-lg tw:md:text-2xl tw:!mb-3">
                  <i className="fas fa-xl fa-id-badge" />
                </div>
                {/* Email */}
                <div>
                  <p className="tw:text-xs tw:md:text-sm tw:font-semibold tw:text-zinc-900 tw:!mb-1 tw:break-all tw:md:break-normal">
                    {assignmentRequest.email}
                  </p>

                  {/* Change email button */}
                  {!(potentialSchedule && potentialScheduleShiftSettings) && (
                    <div className="tw:flex tw:flex-col tw:items-center tw:text-center tw:!mt-1">
                      <button
                        type="button"
                        className="tw:!text-xs tw:text-brand-green-1 tw:underline tw:cursor-pointer tw:!mb-0 tw:bg-transparent tw:border-none tw:p-0"
                        onClick={() => {
                          setInitialAssignmentRequest({ ...assignmentRequest });
                          setInitialUserResponse(userResponse);
                          setAssignmentRequest({});
                          setUserResponse(null);
                          setIsEmailChanging(true);
                          setIsModalOpen(true);
                        }}
                      >
                        Change
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Facility */}
          {(assignmentRequest.facility || assignmentRequest.facility_name) && (
            <div className="tw:flex-1 tw:border-l tw:border-zinc-200 tw:pl-4 tw:lg:border-l-0 tw:lg:pl-0 tw:lg:border-t tw:lg:pt-4">
              <div className="tw:flex tw:flex-col tw:items-center tw:text-center">
                <div className="tw:h-12 tw:w-12 tw:md:h-16 tw:md:w-16  tw:rounded-full tw:bg-brand-green-0/10 tw:flex tw:items-center tw:justify-center tw:text-brand-green-0 tw:text-lg tw:md:text-2xl tw:!mb-3">
                  <i className="fas fa-xl fa-hospital" />
                </div>
                {/* Facility name and city */}
                <div>
                  <p className="tw:text-xs tw:md:text-sm tw:font-semibold tw:text-zinc-900 tw:!mb-1">
                    {assignmentRequest.facility_name || 'Facility'}
                  </p>
                  {assignmentRequest.facility_city && assignmentRequest.facility_state && (
                    <p className="tw:text-[8pt] tw:md:text-xs tw:text-zinc-500 tw:!mb-0">
                      {assignmentRequest.facility_city}, {assignmentRequest.facility_state}
                    </p>
                  )}
                  {/* Change facility button */}
                  {!(potentialSchedule && potentialScheduleShiftSettings) && (
                    <div className="tw:flex tw:flex-col tw:items-center tw:text-center tw:!mt-1">
                      <button
                        type="button"
                        className="tw:!text-xs tw:text-brand-green-1 tw:underline tw:cursor-pointer tw:!mb-0 tw:bg-transparent tw:border-none tw:p-0"
                        onClick={() => {
                          setInitialAssignmentRequest({ ...assignmentRequest });
                          // Prefill tentative facility from current assignmentRequest facility data
                          // We always have facility_name, city, and state set, so use them directly
                          if (assignmentRequest.facility_name) {
                            // If there's no facility ID, it was manually entered, so mark as "Other"
                            if (!assignmentRequest.facility) {
                              setTentativeFacility({
                                id: 'OTHER' as any,
                                name: assignmentRequest.facility_name,
                                city: assignmentRequest.facility_city as any,
                                state: assignmentRequest.facility_state as any,
                              });
                            } else {
                              setTentativeFacility({
                                id: assignmentRequest.facility,
                                name: assignmentRequest.facility_name,
                                city: assignmentRequest.facility_city as any,
                                state: assignmentRequest.facility_state as any,
                              });
                            }
                          } else {
                            setTentativeFacility(null);
                          }
                          setIsModalOpen(true);
                        }}
                      >
                        Change
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>

      <Dialog open={isModalOpen} onClose={handleModalCancel} className="tw:!p-3">
        <form onSubmit={handleSubmit}>
          <DialogTitle className="tw:!text-base tw:!font-semibold tw:!mb-1 tw:p-2 tw:rounded-lg tw:bg-brand-green-0 tw:!text-white ">
            {modalState === MODAL_STATE.FACILITY_SELECT
              ? 'Choose Facility'
              : modalState === MODAL_STATE.MANUAL_INPUT
                ? 'Tell us more about yourself and your facility...'
                : 'Please Identify Yourself'}
          </DialogTitle>
          <DialogBody className="tw:!mt-0">
            <Fieldset>
              {modalState === MODAL_STATE.FACILITY_SELECT ? (
                <>
                  <DialogDescription>
                    Where do you wish to schedule {effectiveProviderData?.display_name}?
                  </DialogDescription>
                  <Field>
                    {/* <Label htmlFor="facility">
                    Facility
                    <span className="tw:text-stoplight-danger">*</span>
                  </Label> */}
                    <Select
                      id="facility"
                      options={facilityOptions}
                      value={tentativeFacilityOption}
                      onChange={(option) => {
                        if (option?.value === 'OTHER') {
                          // Set a special marker for "Other" selection
                          setTentativeFacility({
                            id: 'OTHER' as any,
                            name: '',
                            city: '',
                            state: undefined,
                          });
                        } else {
                          setTentativeFacility(option?.facility || null);
                        }
                      }}
                      placeholder="Start typing to search"
                      className="tw:w-full"
                      styles={reactSelectReset}
                      isClearable={false}
                    />
                  </Field>
                  {isOtherFacilitySelected && (
                    <>
                      <FieldGroup className="tw:mt-4">
                        <Field>
                          <Input
                            id="other-facility-name"
                            type="text"
                            value={tentativeFacility?.name || ''}
                            onChange={(e) =>
                              setTentativeFacility((prev) => ({
                                id: 'OTHER' as any,
                                name: e.target.value,
                                city: prev?.city || '',
                                state: prev?.state,
                              }))
                            }
                            required
                            className="tw:w-full"
                            placeholder="Facility Name"
                          />
                        </Field>
                      </FieldGroup>
                      <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:my-4">
                        <Field>
                          <Input
                            id="other-facility-city"
                            type="text"
                            value={tentativeFacility?.city || ''}
                            onChange={(e) =>
                              setTentativeFacility((prev) => ({
                                id: 'OTHER' as any,
                                name: prev?.name || '',
                                city: e.target.value,
                                state: prev?.state,
                              }))
                            }
                            required
                            className="tw:w-full"
                            placeholder="Facility City"
                          />
                        </Field>
                        <Field>
                          <Input
                            id="other-facility-state"
                            type="text"
                            value={tentativeFacility?.state || ''}
                            onChange={(e) =>
                              setTentativeFacility((prev) => ({
                                id: 'OTHER' as any,
                                name: prev?.name || '',
                                city: prev?.city || '',
                                state: e.target.value as any,
                              }))
                            }
                            required
                            className="tw:w-full"
                            placeholder="Facility State"
                          />
                        </Field>
                      </div>
                    </>
                  )}
                </>
              ) : modalState === MODAL_STATE.MANUAL_INPUT ? (
                <>
                  <DialogDescription className="tw:!mt-0 tw:!pt-0">
                    We did not find a record of your email address. Please enter your name and some
                    information about the facility at which you would like to schedule{' '}
                    {effectiveProviderData?.display_name}.
                  </DialogDescription>
                  <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:my-4">
                    <Field>
                      <Input
                        id="first-name"
                        type="text"
                        value={assignmentRequest.first_name || ''}
                        onChange={(e) =>
                          setAssignmentRequest((prev) => ({ ...prev, first_name: e.target.value }))
                        }
                        className="tw:w-full"
                        placeholder="Your First Name"
                      />
                    </Field>
                    <Field>
                      <Input
                        id="last-name"
                        type="text"
                        value={assignmentRequest.last_name || ''}
                        onChange={(e) =>
                          setAssignmentRequest((prev) => ({ ...prev, last_name: e.target.value }))
                        }
                        className="tw:w-full"
                        placeholder="Your Last Name"
                      />
                    </Field>
                  </div>
                  <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:my-4">
                    <Field>
                      <Input
                        id="title"
                        type="text"
                        value={assignmentRequest.title || ''}
                        onChange={(e) =>
                          setAssignmentRequest((prev) => ({ ...prev, title: e.target.value }))
                        }
                        className="tw:w-full"
                        placeholder="Your Title"
                      />
                    </Field>
                  </div>
                  <FieldGroup>
                    <Field>
                      <Input
                        id="facility-name"
                        type="text"
                        value={tentativeFacility?.name || ''}
                        onChange={(e) =>
                          setTentativeFacility((prev) => ({
                            name: e.target.value,
                            city: prev?.city || '',
                            state: prev?.state,
                          }))
                        }
                        required
                        className="tw:w-full"
                        placeholder="Facility Name"
                      />
                    </Field>
                  </FieldGroup>
                  <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4 tw:my-4">
                    <Field>
                      <Input
                        id="facility-city"
                        type="text"
                        value={tentativeFacility?.city || ''}
                        onChange={(e) =>
                          setTentativeFacility((prev) => ({
                            name: prev?.name || '',
                            city: e.target.value,
                            state: prev?.state,
                          }))
                        }
                        required
                        className="tw:w-full"
                        placeholder="Facility City"
                      />
                    </Field>
                    <Field>
                      <Input
                        id="facility-state"
                        type="text"
                        value={tentativeFacility?.state || ''}
                        onChange={(e) =>
                          setTentativeFacility((prev) => ({
                            name: prev?.name || '',
                            city: prev?.city || '',
                            state: e.target.value as any,
                          }))
                        }
                        required
                        className="tw:w-full"
                        placeholder="Facility State"
                      />
                    </Field>
                  </div>
                </>
              ) : (
                <>
                  <Description>
                    Enter your work email address associated with the organization making the
                    assignment request.
                  </Description>
                  <Field>
                    <Input
                      id="email"
                      type="email"
                      value={assignmentRequest.email || ''}
                      onChange={(e) =>
                        setAssignmentRequest((prev) => ({ ...prev, email: e.target.value }))
                      }
                      placeholder="Email Address"
                      className="tw:w-full"
                    />
                  </Field>
                </>
              )}
            </Fieldset>
          </DialogBody>
          <DialogActions>
            <Button type="button" plain onClick={handleModalCancel}>
              Cancel
            </Button>
            {modalState === MODAL_STATE.FACILITY_SELECT ? (
              <Button
                type="submit"
                color="brandgreen0"
                disabled={
                  !tentativeFacility ||
                  (isOtherFacilitySelected &&
                    (!tentativeFacility?.name?.trim() ||
                      !tentativeFacility?.city?.trim() ||
                      !tentativeFacility?.state))
                }
                // eslint-disable-next-line sonarjs/cognitive-complexity
                onClick={() => {
                  if (tentativeFacility) {
                    if (isOtherFacilitySelected) {
                      // Handle "Other" option - store manual facility info
                      const trimmedName = tentativeFacility.name?.trim();
                      const trimmedCity = tentativeFacility.city?.trim();
                      setAssignmentRequest((prev) => ({
                        ...prev,
                        facility: undefined, // Clear facility ID for manual entry
                        facility_name: trimmedName || null,
                        facility_city: trimmedCity || null,
                        facility_state: tentativeFacility.state
                          ? (tentativeFacility.state as unknown as LicenseStateEnum)
                          : null,
                      }));
                    } else {
                      // Handle regular facility selection
                      setAssignmentRequest((prev) => ({
                        ...prev,
                        facility: tentativeFacility.id || undefined,
                        facility_name: tentativeFacility.name?.trim() || null,
                        facility_city: tentativeFacility.city?.trim() || null,
                        facility_state: tentativeFacility.state
                          ? (tentativeFacility.state as unknown as LicenseStateEnum)
                          : null,
                      }));
                    }
                  }
                  handleModalClose();
                  setInitialAssignmentRequest(null);
                  setInitialUserResponse(null);
                }}
              >
                Continue
              </Button>
            ) : modalState === MODAL_STATE.MANUAL_INPUT ? (
              <Button
                type="submit"
                color="brandgreen0"
                disabled={
                  !tentativeFacility?.name?.trim() ||
                  !tentativeFacility?.city?.trim() ||
                  !tentativeFacility?.state
                }
                onClick={() => {
                  if (tentativeFacility) {
                    const trimmedName = tentativeFacility.name?.trim();
                    const trimmedCity = tentativeFacility.city?.trim();
                    setAssignmentRequest((prev) => ({
                      ...prev,
                      facility: undefined, // Clear facility ID for manual entry
                      facility_name: trimmedName || null,
                      facility_city: trimmedCity || null,
                      facility_state: tentativeFacility.state
                        ? (tentativeFacility.state as unknown as LicenseStateEnum)
                        : null,
                    }));
                    // Close modal and reset tentative facility
                    setIsModalOpen(false);
                    setTentativeFacility(null);
                    setInitialAssignmentRequest(null);
                    setInitialUserResponse(null);
                    // Don't clear user info for anonymous users when facility is set
                    // The facility info should persist
                  } else {
                    handleModalClose();
                    setInitialAssignmentRequest(null);
                    setInitialUserResponse(null);
                  }
                }}
              >
                Continue
              </Button>
            ) : (
              <Button
                type="submit"
                color="brandgreen0"
                disabled={isSubmitting || !assignmentRequest.email?.trim()}
                onClick={() => {
                  // lock in the email address
                  setInitialAssignmentRequest(assignmentRequest);
                  setIsEmailChanging(false);
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Continue'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default ProviderAvailabilityWrapper;
